import React, { useState, useEffect } from 'react';
import './CookieConsent.css';
import { initializeClarity } from '../utils/analytics.js';

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always enabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences and update consent mode
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      updateConsentMode(savedPreferences);
    }
  }, []);

  const updateConsentMode = (prefs) => {
    // Update Google Analytics Consent Mode
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        ad_user_data: prefs.marketing ? 'granted' : 'denied',
        ad_personalization: prefs.marketing ? 'granted' : 'denied',
      });
    }

    if (prefs.analytics) {
      initializeClarity();
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    updateConsentMode(allAccepted);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    updateConsentMode(onlyNecessary);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    updateConsentMode(preferences);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-banner">
        {!showPreferences ? (
          <>
            <div className="cookie-consent-content">
              <h3>🍪 We Value Your Privacy</h3>
              <p>
                We use cookies to enhance your browsing experience, analyze site traffic, 
                and provide personalized content. By clicking "Accept All", you consent to 
                our use of cookies.
              </p>
              <p className="cookie-learn-more">
                Read our <a href="/cookie-policy" target="_blank">Cookie Policy</a> and{' '}
                <a href="/privacy-policy" target="_blank">Privacy Policy</a> to learn more.
              </p>
            </div>
            <div className="cookie-consent-actions">
              <button onClick={handleRejectAll} className="btn-reject">
                Reject All
              </button>
              <button onClick={() => setShowPreferences(true)} className="btn-customize">
                Customize
              </button>
              <button onClick={handleAcceptAll} className="btn-accept">
                Accept All
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-consent-content">
              <h3>Cookie Preferences</h3>
              <p className="cookie-subtitle">
                Choose which cookies you want to allow. You can change these settings at any time.
              </p>

              <div className="cookie-preference-item">
                <div className="cookie-preference-header">
                  <label className="cookie-checkbox">
                    <input
                      type="checkbox"
                      checked={preferences.necessary}
                      disabled
                    />
                    <span className="checkmark disabled"></span>
                    <div className="cookie-label">
                      <strong>Necessary Cookies</strong>
                      <span className="cookie-badge">Always Active</span>
                    </div>
                  </label>
                </div>
                <p className="cookie-description">
                  These cookies are essential for the website to function properly. They enable 
                  core functionality such as security, authentication, and session management. 
                  The website cannot function properly without these cookies.
                </p>
              </div>

              <div className="cookie-preference-item">
                <div className="cookie-preference-header">
                  <label className="cookie-checkbox">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => togglePreference('analytics')}
                    />
                    <span className="checkmark"></span>
                    <div className="cookie-label">
                      <strong>Analytics Cookies</strong>
                    </div>
                  </label>
                </div>
                <p className="cookie-description">
                  These cookies help us understand how visitors interact with our website by 
                  collecting and reporting information anonymously. This helps us improve our 
                  website and provide better user experience.
                </p>
              </div>

              <div className="cookie-preference-item">
                <div className="cookie-preference-header">
                  <label className="cookie-checkbox">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={() => togglePreference('marketing')}
                    />
                    <span className="checkmark"></span>
                    <div className="cookie-label">
                      <strong>Marketing Cookies</strong>
                    </div>
                  </label>
                </div>
                <p className="cookie-description">
                  These cookies are used to track visitors across websites and display ads 
                  that are relevant and engaging. They may also be used to measure the 
                  effectiveness of advertising campaigns.
                </p>
              </div>
            </div>

            <div className="cookie-consent-actions">
              <button onClick={() => setShowPreferences(false)} className="btn-back">
                Back
              </button>
              <button onClick={handleRejectAll} className="btn-reject">
                Reject All
              </button>
              <button onClick={handleSavePreferences} className="btn-accept">
                Save Preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CookieConsent;
